import React, { useState } from "react";
import { Alert, BreadcrumbItem, Card, CardBody, Col, Container, FormGroup, Input, Label, Row, Button as RButton } from "reactstrap";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import * as Loading from "@repoflow/components/loading";
import * as ProfileQL from "queries/profile";
import { formatNumber } from "components/day/summary";
import {
  age as ageField,
  gender as genderField,
  weight as weightField,
  height as heightField,
  activity as activityField,
  goal as goalField,
  customTarget as customField,
  proteinTarget as proteinField,
  carbsTarget as carbsField,
  fatTarget as fatField,
  MACROS,
  macroField,
  GENDERS,
  ACTIVITIES,
  GOALS,
} from "@common/schema/profile/fields";
import { calculate, FLOOR, macroSuggestion } from "@common/schema/profile/target";
import "components/day/day.css";

export const Sidebar = () => null;

export const Indice = () => <BreadcrumbItem>Daily target</BreadcrumbItem>;

// What the screen holds while it is being typed in: every field a string, as
// an input hands it over, so a half-typed "7" is not coerced into anything.
const EMPTY = {
  age: "",
  gender: "",
  weight: "",
  height: "",
  activity: "",
  goal: "maintain",
  customTarget: "",
  proteinTarget: "",
  carbsTarget: "",
  fatTarget: "",
};

const MACRO_FIELDS = { protein: proteinField, carbs: carbsField, fat: fatField };

const gramsOf = (value) => (value === null || value === undefined ? "" : String(value));

const fromProfile = (profile) =>
  profile
    ? {
        age: String(profile.age),
        gender: profile.gender,
        weight: String(profile.weight),
        height: String(profile.height),
        activity: profile.activity,
        goal: profile.goal,
        customTarget: profile.customTarget ? String(profile.customTarget) : "",
        proteinTarget: gramsOf(profile.proteinTarget),
        carbsTarget: gramsOf(profile.carbsTarget),
        fatTarget: gramsOf(profile.fatTarget),
      }
    : EMPTY;

// What `save` takes. An empty custom target is null — "use the calculator's";
// an empty macro target is null — "none".
const gramsInput = (value) => (value === "" || value === null || value === undefined ? null : parseFloat(value));

export const toInput = (values, custom) => ({
  age: parseInt(values.age, 10),
  gender: values.gender,
  weight: parseFloat(values.weight),
  height: parseFloat(values.height),
  activity: values.activity,
  goal: values.goal,
  customTarget: custom && values.customTarget !== "" ? parseFloat(values.customTarget) : null,
  proteinTarget: gramsInput(values.proteinTarget),
  carbsTarget: gramsInput(values.carbsTarget),
  fatTarget: gramsInput(values.fatTarget),
});

// The kcal the macro targets add up to, for the line under them: 4 per gram
// of protein and carbs, 9 per gram of fat. Null while none is set.
export const macroKcal = (values) => {
  const grams = MACROS.map((m) => Number(values[macroField(m)]) || 0);
  if (!grams.some((g) => g > 0)) return null;
  const [protein, carbs, fat] = grams;
  return Math.round(protein * 4 + carbs * 4 + fat * 9);
};

// The six things the target is worked out from, and the calculator beside
// them. Plain controlled inputs rather than form-schema's form: the result
// card reads every keystroke, and the calculator is the same module the
// server runs on save (@common/schema/profile/target), so what is shown here
// is what gets stored.
export const Content = () => {
  const { data, loading, error } = useQuery(ProfileQL.get, { fetchPolicy: "cache-and-network" });
  const profile = data?.viewer?.account?.profile?.get || null;

  if (error) {
    return (
      <Container className="noshchat-day mt-4">
        <Alert color={"danger"}>The profile could not be read. {error.message}</Alert>
      </Container>
    );
  }

  if (!data && loading) {
    return <div className="mt-4"><Loading.Icon /></div>;
  }

  // Started once from what was read. The refetch after a save brings back
  // what the form already holds, so it is not re-read into it, and the
  // "Saved" line stays.
  return <TargetForm profile={profile} />;
};

const TargetForm = ({ profile }) => {
  const [values, setValues] = useState(() => fromProfile(profile));
  const [custom, setCustom] = useState(Boolean(profile?.customTarget));
  const [saved, setSaved] = useState(null);
  const [failed, setFailed] = useState(null);

  const [save, { loading: saving }] = useMutation(ProfileQL.save, ProfileQL.AFTER_SAVE);

  const set = (name) => (event) => {
    setSaved(null);
    setValues((all) => ({ ...all, [name]: event.target.value }));
  };

  const result = calculate(values);
  const target = custom && Number(values.customTarget) > 0 ? Number(values.customTarget) : result?.calculated;

  const submit = async (event) => {
    event.preventDefault();
    if (!result || saving) return;
    setFailed(null);

    try {
      const { data } = await save({ variables: { input: toInput(values, custom) } });
      setSaved(data.viewer.account.profile.save.target);
    } catch (error) {
      setFailed(String(error.message || error));
    }
  };

  return (
    <Container className="noshchat-day mt-4">
      <h4 className="mb-1">Your daily target</h4>
      <p className="text-muted">
        Tell it about you and it works out how many calories a day to aim for. The chat and the
        food log measure each day against it.
      </p>

      <form onSubmit={submit} data-testid={"target-form"}>
        <Card>
          <CardBody>
            <Row>
              <Col xs={6} md={3}>
                <NumberField id={"age"} field={ageField} value={values.age} onChange={set("age")} step={1} />
              </Col>
              <Col xs={6} md={3}>
                <SelectField id={"gender"} field={genderField} values={GENDERS} value={values.gender} onChange={set("gender")} />
              </Col>
              <Col xs={6} md={3}>
                <NumberField id={"weight"} field={weightField} value={values.weight} onChange={set("weight")} step={0.1} />
              </Col>
              <Col xs={6} md={3}>
                <NumberField id={"height"} field={heightField} value={values.height} onChange={set("height")} step={0.5} />
              </Col>
            </Row>
            <Row>
              <Col xs={12} md={7}>
                <SelectField id={"activity"} field={activityField} values={ACTIVITIES} value={values.activity} onChange={set("activity")} />
              </Col>
              <Col xs={12} md={5}>
                <SelectField id={"goal"} field={goalField} values={GOALS} value={values.goal} onChange={set("goal")} />
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card className="mt-3" data-testid={"target-result"}>
          <CardBody>
            {result ? (
              <>
                <div className="noshchat-total__kcal" data-testid={"target-kcal"}>{formatNumber(target)}</div>
                <div className="text-muted">kcal a day{custom && values.customTarget ? ", your own number" : ""}</div>

                <ul className="list-unstyled small text-muted mt-3 mb-0">
                  <li>Burnt at rest (BMR): <strong>{formatNumber(result.bmr)}</strong> kcal</li>
                  <li>With your activity: <strong data-testid={"target-maintenance"}>{formatNumber(result.maintenance)}</strong> kcal to keep your weight</li>
                  {result.adjustment ? (
                    <li>Your goal: <strong>{result.adjustment > 0 ? "+" : "−"}{formatNumber(Math.abs(result.adjustment))}</strong> kcal</li>
                  ) : null}
                  <li>Calculated target: <strong data-testid={"target-calculated"}>{formatNumber(result.calculated)}</strong> kcal</li>
                </ul>

                {result.floored ? (
                  <Alert color={"warning"} className="mt-3 mb-0 small">
                    The formula came out under {formatNumber(FLOOR)} kcal, so the target is held at{" "}
                    {formatNumber(FLOOR)}. Eating less than that is usually only advised with a doctor.
                  </Alert>
                ) : null}
              </>
            ) : (
              <div className="text-muted" data-testid={"target-incomplete"}>
                Fill in every field above to see your target.
              </div>
            )}

            <FormGroup check className="mt-3">
              <Label check>
                <Input type={"checkbox"} checked={custom} onChange={(e) => { setSaved(null); setCustom(e.target.checked); }} data-testid={"target-custom-toggle"} />{" "}
                Set my own number instead
              </Label>
            </FormGroup>
            {custom ? (
              <Row className="mt-2">
                <Col xs={12} md={5}>
                  <NumberField id={"customTarget"} field={customField} value={values.customTarget} onChange={set("customTarget")} step={10} />
                </Col>
              </Row>
            ) : null}
          </CardBody>
        </Card>

        <MacroCard
          values={values}
          set={set}
          suggestion={macroSuggestion({ target, weight: values.weight })}
          onSuggest={(s) => {
            setSaved(null);
            setValues((all) => ({ ...all, proteinTarget: String(s.protein), carbsTarget: String(s.carbs), fatTarget: String(s.fat) }));
          }}
          onClear={() => {
            setSaved(null);
            setValues((all) => ({ ...all, proteinTarget: "", carbsTarget: "", fatTarget: "" }));
          }}
        />

        {failed ? <Alert color={"danger"} className="mt-3">The target could not be saved. {failed}</Alert> : null}
        {saved ? (
          <Alert color={"success"} className="mt-3" data-testid={"target-saved"}>
            Saved. Your target is {formatNumber(saved)} kcal a day. <Link to={`/chat`}>Back to the chat</Link>
          </Alert>
        ) : null}

        <div className="mt-3 mb-4">
          <RButton type={"submit"} color={"primary"} disabled={!result || saving} data-testid={"target-save"}>
            {saving ? "Saving…" : "Save target"}
          </RButton>
        </div>
      </form>
    </Container>
  );
};

// Grams a day of each macro, each optional. A suggested split of the calorie
// target fills all three in one click, as a start; what is saved is what the
// boxes hold.
const MacroCard = ({ values, set, suggestion, onSuggest, onClear }) => {
  const kcal = macroKcal(values);
  const any = MACROS.some((m) => values[macroField(m)] !== "");
  return (
    <Card className="mt-3" data-testid={"target-macros"}>
      <CardBody>
        <h6 className="mb-1">Macro targets <span className="text-muted small">(optional)</span></h6>
        <p className="text-muted small mb-2">
          Grams a day. The food log and the chat measure each one you set.
        </p>
        <Row>
          {MACROS.map((m) => (
            <Col xs={4} key={m}>
              <NumberField id={macroField(m)} field={MACRO_FIELDS[m]} value={values[macroField(m)]} onChange={set(macroField(m))} step={1} />
            </Col>
          ))}
        </Row>
        {kcal !== null ? (
          <div className="small text-muted" data-testid={"target-macros-kcal"}>
            These add up to {formatNumber(kcal)} kcal.
          </div>
        ) : null}
        <div className="mt-2">
          {suggestion ? (
            <RButton type={"button"} color={"link"} size={"sm"} className="pl-0" onClick={() => onSuggest(suggestion)} data-testid={"target-macros-suggest"}>
              Use {suggestion.protein} g protein, {suggestion.carbs} g carbs, {suggestion.fat} g fat
            </RButton>
          ) : null}
          {any ? (
            <RButton type={"button"} color={"link"} size={"sm"} onClick={onClear} data-testid={"target-macros-clear"}>
              Clear
            </RButton>
          ) : null}
        </div>
        {suggestion ? (
          <div className="small text-muted">
            The suggestion: protein at 1.6 g per kg of body weight, fat at 30% of your calories, carbs the rest.
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
};

const NumberField = ({ id, field, value, onChange, step }) => (
  <FormGroup>
    <Label for={`target-${id}`} className="small text-muted mb-1">{field.label}</Label>
    <Input
      id={`target-${id}`}
      type={"number"}
      inputMode={"decimal"}
      min={field.minimum}
      max={field.maximum}
      step={step}
      value={value}
      onChange={onChange}
      data-testid={`target-${id}`}
    />
  </FormGroup>
);

const SelectField = ({ id, field, values, value, onChange }) => (
  <FormGroup>
    <Label for={`target-${id}`} className="small text-muted mb-1">{field.label}</Label>
    <Input id={`target-${id}`} type={"select"} value={value} onChange={onChange} data-testid={`target-${id}`}>
      {value === "" ? <option value={""}>Choose…</option> : null}
      {values.map((v, i) => (
        <option key={v} value={v}>{field.enumLabels[i].label}</option>
      ))}
    </Input>
  </FormGroup>
);
